import Button from '../../../components/base/Button';
import LiveStats from '../../../components/feature/LiveStats';
import { serverEndpoints } from '@/config/serverEndpoints';
import { useScrollReveal } from '@/hooks/useScrollReveal';

/**
 * HeroSection component displaying the main landing section.
 *
 * @description The hero section serves as the primary landing area with the site title,
 * tagline, call-to-action buttons, live server stats, and a scroll indicator.
 * Features a dark horror-themed background with gradient overlays.
 *
 * @returns {JSX.Element} The hero section component
 */
export default function HeroSection() {
  const sectionRef = useScrollReveal();

  const handleJoinServer = () => {
    window.open(serverEndpoints.direct.steamUrl, '_self');
  };

  /**
   * Scrolls smoothly to the scoreboard section.
   *
   * @description Finds the scoreboard section element and scrolls to it using
   * smooth scrolling behavior. Used by the scroll-down arrow indicator.
   */
  const handleScrollToNext = () => {
    const element = document.getElementById('scoreboard');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="home"
      className="min-h-screen flex items-center justify-center relative overflow-hidden"
    >
      {/* Full-bleed KF1 key art — the dark/horror twin of the Uliunai hub plaza.
          The mask fades the bottom of the image into the page so it blends
          smoothly into the next section (the ember particles bridge the gap). */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: "url('/brand/kf1-hero.png')",
          maskImage: 'linear-gradient(to bottom, black 55%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to bottom, black 55%, transparent 100%)',
        }}
      />
      {/* Subtle top darken for nav/text legibility (bottom is handled by the mask) */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-transparent z-[1]"></div>

      <div className="container mx-auto px-4 relative z-10" ref={sectionRef}>
        <div className="text-center max-w-4xl mx-auto">
          {/* Emblem (shared Uliunai mark) — bobs like the hub & Minecraft heroes */}
          <img
            src="/brand/ul-logo.png"
            alt="Uliunai"
            className="w-24 h-24 md:w-32 md:h-32 object-contain mx-auto mb-4 animate-float drop-shadow-[0_6px_24px_rgba(220,38,38,0.45)]"
          />

          {/* KILLING FLOOR wordmark — light metallic with red glow */}
          <h1 className="text-5xl md:text-7xl font-black mb-4 font-orbitron uppercase tracking-tight bg-gradient-to-b from-white via-gray-300 to-gray-500 bg-clip-text text-transparent drop-shadow-[0_2px_18px_rgba(220,38,38,0.35)]">
            Killing Floor
          </h1>

          {/* Subtitle with red accent rules — family-consistent with the hub & Minecraft */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="h-[2px] w-10 sm:w-16 bg-gradient-to-r from-transparent to-red-600" />
            <span className="font-orbitron text-xs sm:text-sm font-bold uppercase tracking-[0.4em] text-gray-300">
              Uliunai Server
            </span>
            <span className="h-[2px] w-10 sm:w-16 bg-gradient-to-l from-transparent to-red-600" />
          </div>

          <p className="scroll-reveal text-xl md:text-2xl text-gray-300 mb-6 font-rajdhani">
            The <strong className="text-red-400">Longest-Running</strong> Killing Floor 1 Server
          </p>

          <p className="scroll-reveal text-lg text-gray-200 mb-12 max-w-2xl mx-auto [text-shadow:_0_2px_12px_rgba(0,0,0,0.85)]">
            Join our legendary community with custom weapons, maps, monsters, and years of non-stop action.
            Experience the ultimate Killing Floor adventure with friendly players and active admins.
          </p>

          <div className="scroll-reveal flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
            <Button
              variant="primary"
              size="lg"
              onClick={handleJoinServer}
              className="pulse-glow flex items-center gap-3"
            >
              <i className="ri-gamepad-fill text-xl"></i>
              Join Server Now
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => document.getElementById('server-info')?.scrollIntoView({ behavior: 'smooth' })}
              className="flex items-center gap-3"
            >
              <i className="ri-information-line text-xl"></i>
              Server Info
            </Button>
          </div>

          <div className="scroll-reveal max-w-3xl mx-auto">
            <LiveStats />
          </div>
        </div>
      </div>

      <div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce cursor-pointer hover:text-red-400 transition-colors z-10"
        onClick={handleScrollToNext}
        role="button"
        aria-label="Scroll to next section"
      >
        <i className="ri-arrow-down-line text-white text-2xl"></i>
      </div>
    </section>
  );
}
